import type { ChronologyEvent } from "~/api/model";

type WhatFields = Pick<ChronologyEvent, "eventType" | "documentCategory">;
type TitleFields = Pick<
  ChronologyEvent,
  "detail" | "eventType" | "documentCategory"
>;

/**
 * Builds the eyebrow label: "EVENT_TYPE · DOCUMENT_CATEGORY" or either alone.
 * Returns empty string when both are blank (the eyebrow row hides).
 */
export const buildWhatLabel = (event: WhatFields): string =>
  [event.eventType, event.documentCategory]
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .join(" · ");

/**
 * Builds the title text. When `detail` is non-empty, use it directly.
 * When empty, promote the whatLabel (eventType · documentCategory) so the
 * item never reads as blank.
 */
export const buildTitleText = (event: TitleFields): string => {
  const detail = event.detail.trim();
  if (detail.length > 0) return detail;
  return buildWhatLabel(event);
};

/**
 * Whether the eyebrow should render. It hides when detail is empty (the
 * what label promotes to title in that case, avoiding duplication).
 */
export const shouldShowEyebrow = (event: TitleFields): boolean => {
  const detail = event.detail.trim();
  if (detail.length === 0) return false;
  return buildWhatLabel(event).length > 0;
};
